import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {DealListConfig, Profile, ProfilesService, User, UserLevels, UserService} from '../core';
import {concatMap, tap} from 'rxjs/operators';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import AssetsUtil from '../core/utils/assets.util';
import {BehaviorSubject} from 'rxjs';
import {CustomValidators} from '../auth/custom-validator';
import AuthUtil from '../core/utils/auth.util';
import {ConfirmationService, MessageService} from 'primeng/api';
import {ErrorUtil} from '../core/utils/error.util';
import {TranslateService} from '@ngx-translate/core';
import {AuthComponent} from '../auth/auth.component';
import {DialogService} from 'primeng/dynamicdialog';
import {Meta, Title} from '@angular/platform-browser';
import {environment} from '../../environments/environment';
import {ServerResponse} from '../core/models/server-response.model';

@Component({
    selector: 'app-profile-page',
    templateUrl: './profile.component.html',
    providers: [MessageService, ConfirmationService, DialogService]
})
export class ProfileComponent implements OnInit {
    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private fb: FormBuilder,
        private messageService: MessageService,
        private profileService: ProfilesService,
        private translation: TranslateService,
        private confirmationService: ConfirmationService,
        private dialogService: DialogService,
        private metaService: Meta,
        private titleService: Title,
    ) { }
    get f() {
        return this.authForm.controls;
    }

    user: User = {} as User;
    settingsForm: FormGroup;
    errors: Object = {};
    isSubmitting = false;
    avatarUrl = '';
    fallbackImg = AssetsUtil.loadAvatarFallbackOnError();
    personalInfoSelected = true;
    pubDealsSelected = false;
    commentsSelected = false;
    settingsSelected = false;
    listConfig: BehaviorSubject<DealListConfig> = new BehaviorSubject<DealListConfig>(null);
    profile: Profile;
    isUser = false;
    isAdmin = false;
    authForm: FormGroup;
    serverErrorId = null;
    isDeleted = false;
    isAuthenticated = false;
    ref = null;
    followersSelected = false;
    followingSelected = false;
    followers: [Profile];
    following: [Profile];
    filtersNotificationSelected = false;
updatingInformation = false;
    ngOnInit() {
        this.route.data.pipe(
            concatMap((data: {profile: Profile}) => {
                this.profile = data.profile;
                this.titleService.setTitle(this.translation.instant('seo_title_user') + this.profile.user_login);
                this.setMetaTags();
                // Load the current user's data.
                this.avatarUrl = AssetsUtil.get_orig_avatar_url(this.profile.user_id);
                this.isDeleted = (this.profile.user_level === UserLevels.Deleted);
                return this.userService.currentUser.pipe(tap(
                    (userData: User) => {
                        this.profileService.get(this.profile.user_login).pipe(tap(
                            specificProfile => {
                                this.user = userData;
                                this.isUser = (this.user.username === this.profile.user_login);
                                this.isAdmin = (this.user.level === UserLevels.Admin);
                                this.setupRooting();
                            }
                        )).subscribe();
                    }
                ));
            })
        ).subscribe();
        this.userService.isAuthenticated.pipe(tap( next =>
            this.isAuthenticated = next.valueOf()
        )).subscribe();
        this.route.url.subscribe(next => {
            this.setupRooting();
        });
        // Make a fresh copy of the current user's object to place in editable form fields
        Object.assign(this.user, this.userService.getCurrentUser());
        // Fill the form
        // this.settingsForm.patchValue(this.user);
    }

    setupRooting() {
        const routerParams = this.router.url.split('/');
        if (routerParams.length > 3) {
            // 1 is the type category/search etc.
            this.titleService.setTitle(this.translation.instant('seo_title_user') + this.profile.user_login);
            switch (routerParams[3]) {
                case 'deals':
                    this.titleService.setTitle(this.translation.instant('seo_title_category_profile_store') + this.profile.user_login);
                    const pageParams = {fn: 'deals', filters: {authorID: this.profile.user_id, page: Number(routerParams[4])}};
                    this.listConfig.next(pageParams);
                    this.menuChange(1);
                    break;
                case 'comments':
                    if (!this.isUser) { return; }
                    const commentsParams = {fn: 'deals', filters: {authorID: this.profile.user_id, page: Number(routerParams[4])}};
                    this.listConfig.next(commentsParams);
                    this.menuChange(2);
                    break;
                case 'settings':
                    if (!this.isUser && !this.isAdmin) { return; }
                    this.menuChange(3);
                    break;
                case 'followers':
                    this.profileService.followers(this.profile.user_id).pipe(tap( data =>
                        this.followers = data
                    )).subscribe();
                    this.menuChange(4);
                    break;
                case 'following':
                    this.profileService.following(this.profile.user_id).pipe(tap( data =>
                        this.following = data
                    )).subscribe();
                    this.menuChange(5);
                    break;
                case 'notifications':
                    if (!this.isUser) { return; }
                    this.menuChange(6);
                    break;
                default:
                    this.menuChange(0);
                    break;
            }
        } else {
            this.menuChange(0);
        }
        this.setMetaTags();
    }

    logout() {
        this.userService.purgeAuth();
        this.router.navigateByUrl('/');
    }
    getFollowAvatar(valueID) {
        return AssetsUtil.get_orig_avatar_url(valueID);
    }
    setMetaTags() {
        this.metaService.addTags([
            {name: 'og:title', content: this.titleService.getTitle()},
            {name: 'og:type', content: 'deal'},
            {name: 'og:url', content: environment.server_url + this.router.url},
            {name: 'og:image', content: environment.assets_url + '/images/dlnd_square_icon.png'},
            {name: 'twitter:title', content: this.titleService.getTitle()},
            {name: 'twitter:url', content: environment.server_url + this.router.url},
            {name: 'twitter:image', content: environment.assets_url + '/images/dlnd_square_icon.png'},
            {name: 'description', content: this.translation.instant('seo_description_generic')}
        ]);
    }
    requestLogin() {
        this.ref = this.dialogService.open(AuthComponent, {
            styleClass: 'login-popup',
            dismissableMask: true,
            data: 'login'
        });
        this.ref.onClose.subscribe( data =>
            this.ref.destroy()
        );
    }
    updateUser(values: Object) {
        Object.assign(this.user, values);
    }
    submitPasswordForm() {
        this.isSubmitting = true;
        // stop here if form is invalid
        if (this.authForm.invalid) {
            return;
        }

        this.errors = {errors: {}};
        const pwd = this.authForm.value.currentPassword;
        const newPwd = this.authForm.value.newPassword;
        const credentials = { 'currentPassword' : AuthUtil.generateEncryptedPwd(pwd), 'newPassword': AuthUtil.generateEncryptedPwd(newPwd)};
        this.userService
            .updatePassword(credentials)
            .subscribe(
                data => {
                    if (data.ok) {
                        this.isSubmitting = false;
                        this.messageService.add({severity: 'success', summary: this.translation.instant('password_change_ok')});
                        this.authForm.reset();
                    }
                    return data;
                },
                err => {
                    this.errors = err;
                    this.serverErrorId = err['error'];
                    this.isSubmitting = false;
                    if (this.serverErrorId !== 2) {
                        this.messageService.add({severity: 'error',
                            summary: this.translation.instant('error'),
                            detail: this.translation.instant(new ErrorUtil(this.serverErrorId).description)
                        });
                    }
                }
            );
    }
    menuChange(number: number) {
        this.personalInfoSelected = (number === 0);
        this.settingsSelected = (number === 3);
        this.pubDealsSelected = (number === 1);
        this.commentsSelected = (number === 2);
        this.followersSelected = (number === 4);
        this.followingSelected = (number === 5);
        this.filtersNotificationSelected = (number === 6);
        if (this.settingsSelected) {
            this.authForm = this.fb.group({
                'currentPassword': ['', Validators.required],
                'newPassword': ['', Validators.required],
                'newPasswordConfirm': ['', Validators.required]
            });
            this.authForm.setValidators(CustomValidators.mustMatch('newPassword', 'newPasswordConfirm'));
        }
    }
    follow() {
        if (!this.isAuthenticated) {
            this.requestLogin();
            return;
        }
        this.profileService
            .follow(this.profile.user_login)
            .subscribe(data => {
                    if (data['ok']) {
                        this.profile.isFollowing = true;
                        this.profile.followerCount += 1;
                    }
                    return data;
                }, error =>  {
                    this.messageService.add({severity: 'error',
                        summary: this.translation.instant('error'),
                        detail: this.translation.instant( new ErrorUtil(error['error']).description)
                    });
                    return error;
                }
            );
    }
    unfollow() {
        if (!this.isAuthenticated) {
            this.requestLogin();
            return;
        }
        this.profileService
            .unfollow(this.profile.user_login)
            .subscribe(data => {
                    if (data['ok']) {
                        this.profile.isFollowing = false;
                        this.profile.followerCount -= 1;
                    }
                    return data;
                }, error =>  {
                    this.messageService.add({severity: 'error',
                        summary: this.translation.instant('error'),
                        detail: this.translation.instant( new ErrorUtil(error['error']).description)
                    });
                    return error;
                }
            );
    }

    deletePressed() {
        this.confirmationService.confirm({
            message: this.translation.instant('profile_delete_message'),
            header: this.translation.instant('proceed_prompt'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translation.instant('profile_delete_yes'),
            acceptIcon: 'pi pi-times',
            defaultFocus: 'none',
            dismissableMask: true,
            accept: () => {
                this.profileService.delete(this.profile.user_id).pipe(tap(
                    data => {
                        if (data.ok) {
                            this.messageService.add({severity: 'info',
                                detail: this.translation.instant('profile_delete_ok')});
                            setTimeout(() => {
                                this.router.navigateByUrl('/');
                            }, 2000);
                        } else {
                            this.messageService.add({severity: 'error',
                                summary: this.translation.instant('error'),
                                detail: this.translation.instant(new ErrorUtil(0).description)});
                        }
                    },
                    error => {
                        this.messageService.add({severity: 'error',
                            summary: this.translation.instant('error'),
                            detail: this.translation.instant(new ErrorUtil(error['error']).description)});
                    }
                )).subscribe();

            }
        });
    }
    editPersonalInformation() {
        this.updatingInformation = true;
        const user = {twitter: this.profile.user_twitter,
            facebook: this.profile.user_facebook,
            instagram: this.profile.user_instagram,
            names: this.user.names};
        this.userService.update(user).pipe(tap( next => {
            this.handleUpdateSuccess(next);
            this.updatingInformation = false;
        }, error => this.handleError(error))).subscribe();
    }
    handleUpdateSuccess(server: ServerResponse, avatar = false) {
        if (server.ok) {
            if (avatar) {
                this.messageService.add({severity: 'success', detail: this.translation.instant('profile_avatar_update_ok')});
                this.avatarUrl = server.avatarURL;
            } else {
                this.messageService.add({severity: 'success', detail: this.translation.instant('profile_information_update_ok')});
            }
        } else {
            this.handleError(server);
        }
    }
    handleError(response: ServerResponse) {
        this.updatingInformation = false;
        this.messageService.add({severity: 'error', summary: this.translation.instant('error'),
            detail: this.translation.instant(new ErrorUtil(response['error']).description)});
    }

    changeAvatar(event, fileUpload) {
        if (event?.currentFiles[0] !== undefined) {
            if (event?.currentFiles[0].size > 2000000) {
                this.messageService.add({severity: 'error', summary: this.translation.instant('error'),
                    detail: this.translation.instant('profile_avatar_too_big')});
            } else {
                this.userService.updateAvatar(event?.currentFiles[0]).pipe(tap(
                    next => this.handleUpdateSuccess(next, true),
                    error => this.handleError(error)
                )).subscribe();
            }
        } else {
            this.messageService.add({severity: 'error', summary: this.translation.instant('error'),
                detail: this.translation.instant('profile_avatar_invalid')});
        }
        fileUpload.clear();
    }

    changeAvatarError($event: any) {
        console.log($event);
    }
}
